import { z } from 'zod/v3';
import {
  GenericToolset,
  RawCanvasData,
  CanvasNode,
  WorkflowVariable,
  WorkflowTask,
  WorkflowPlan,
  ModelInfo,
} from '@refly/openapi-schema';
import { genNodeEntityId, genUniqueId } from '@refly/utils';
import { CanvasNodeFilter } from './types';
import { prepareAddNode } from './utils';

// Task schema for workflow plan
/** socialPublish 的单个平台目标：平台 + 可选账号限定 + 可选定时（二选一）。 */
export const socialPublishTargetSchema = z.object({
  platformType: z.number().int().min(1).max(20).describe('Platform code, same as platformType.'),
  userPrompt: z
    .string()
    .max(10000)
    .optional()
    .describe(
      'User intent for tasks on this platform. Generate a concrete instruction preserving the user request; users can edit it per account before execution. Platform operation rules are appended by the executor.',
    ),
  accountIds: z
    .array(z.string())
    .optional()
    .describe('Optional subset of selected account IDs for this platform. Usually omit.'),
  publishAt: z
    .string()
    .optional()
    .describe('Absolute publish time, ISO 8601 with timezone (e.g. 2026-09-12T09:00:00+08:00).'),
  delayMinutes: z
    .number()
    .min(0)
    .max(1440)
    .optional()
    .describe(
      'Only for an explicitly requested initial delay. Never encode account spacing here: for two accounts ten minutes apart use node intervalMinutes=10 and omit delayMinutes for ALL platforms. Ignored when publishAt is set.',
    ),
  mediaVariableId: z.string().optional().describe('Per-platform uploaded media input variable.'),
  requestVariableId: z.string().optional().describe('Per-platform copy input variable.'),
  intervalMinutes: z
    .number()
    .min(0)
    .max(1440)
    .optional()
    .describe('Per-platform account interval.'),
});

export const publishTaskSettingsSchema = z.object({
  publishMode: z.enum(['immediate', 'scheduled', 'delay']).optional(),
  publishAt: z.string().optional(),
  delayMinutes: z.number().min(0).max(1440).optional(),
  intervalMinutes: z.number().min(0).max(1440).optional(),
  mediaVariableId: z.string().optional(),
  requestVariableId: z.string().optional(),
});

export const workflowTaskSchema = z.object({
  taskSettings: z
    .record(publishTaskSettingsSchema)
    .optional()
    .describe('Per-account publishing configuration; preserve user edits.'),
  taskPrompts: z
    .record(z.string().max(10000))
    .optional()
    .describe(
      'Per-account user intent overrides, keyed by selected account ID. Preserve existing user edits.',
    ),
  id: z.string().describe('Unique ID for the task'),
  title: z.string().describe('Display title for the task'),
  prompt: z.string().describe('The prompt or instruction for this task'),
  dependentTasks: z
    .array(z.string().describe('Task ID'))
    .optional()
    .describe('Tasks that must be executed before this task'),
  toolsets: z.array(z.string().describe('Toolset ID')).describe('Toolsets selected for this task'),
  mediaType: z
    .enum(['image', 'video'])
    .optional()
    .describe(
      'Media type for generation tasks. When set, this task creates image/video nodes instead of skillResponse nodes.',
    ),
  batchCount: z
    .number()
    .min(1)
    .max(20)
    .default(1)
    .describe('Number of media items to generate in batch. Only applicable when mediaType is set.'),
  model: z
    .string()
    .optional()
    .describe(
      'Model ID (e.g. gpt-image-2, sora-2). Required for media tasks (mediaType set); optional for regular tasks to pin an LLM from the available LLM list.',
    ),
  providerItemId: z
    .string()
    .optional()
    .describe(
      'Provider item ID. Required for media tasks (media generation provider item). For regular tasks, optionally reference an LLM provider item from the available LLM list; when omitted, the system default model is used.',
    ),
  skillId: z
    .string()
    .optional()
    .describe(
      'Workflow skill ID to bind to this task node. Must be copied verbatim from the available workflow skill inventory; never invent one.',
    ),
  promptSkillIds: z
    .array(z.string())
    .optional()
    .describe(
      'Prompt skill IDs to bind to this task node. Multiple are allowed. Each ID must be copied verbatim from the available prompt skill inventory; never invent one. The skill instructions are expanded into the node agent prompt at execution time.',
    ),
  // Production Studio and socialPublish are the two dedicated media workflow nodes.
  nodeType: z
    .enum(['productionStudio', 'socialPublish'])
    .optional()
    .describe(
      'Special node type. productionStudio creates the Production Studio node; socialPublish creates a direct publishing node. Mutually exclusive with mediaType.',
    ),
  visionProviderItemId: z
    .string()
    .optional()
    .describe(
      'Vision-capable LLM provider item ID for browser tasks such as socialPublish. Choose only from the available vision model list.',
    ),
  strategy: z
    .enum([
      'highlight-montage',
      'story-narration',
      'series-recommendation',
      'overlay-only',
      'assemble',
      'speech_cleanup',
      'highlight',
    ])
    .optional()
    .describe('Production strategy. Only applicable when nodeType="productionStudio".'),
  strategyMarket: z
    .enum(['zh-CN', 'en-US', 'fil-PH', 'id-ID', 'th-TH', 'vi-VN'])
    .optional()
    .describe('Versioned video-production market pack for productionStudio.'),
  mediaVariableId: z.string().optional().describe('Stable media input variable ID.'),
  sourceVariableIds: z
    .array(z.string())
    .max(20)
    .optional()
    .describe('Ordered stable media input variable IDs for productionStudio.'),
  sourceNodeIds: z
    .array(z.string())
    .max(20)
    .optional()
    .describe('Ordered upstream task IDs for productionStudio.'),
  requestVariableId: z.string().optional().describe('Stable text requirement variable ID.'),
  targetPlatform: z.string().max(64).optional(),
  targetDurationSeconds: z.number().int().min(1).max(600).optional(),
  aspectRatio: z.enum(['9:16', '16:9', '1:1', '4:5']).optional(),
  resolution: z
    .object({
      width: z.number().int().min(16).max(7680),
      height: z.number().int().min(16).max(7680),
    })
    .strict()
    .optional(),
  includeSubtitles: z.boolean().optional(),
  musicAssetId: z.string().max(128).optional(),
  accountVariableId: z.string().optional().describe('Stable social-account input variable ID.'),
  platformType: z
    .number()
    .int()
    .min(1)
    .max(20)
    .optional()
    .describe(
      'Target social platform code. 1=小红书, 2=视频号, 3=抖音, 4=快手, 5=TikTok, 6=Instagram, 7=Facebook, 8=B站, 9=百家号, 10=YouTube, 11=Twitter. Only applicable when nodeType="socialPublish".',
    ),
  postCount: z
    .number()
    .int()
    .min(1)
    .max(10)
    .optional()
    .describe(
      'Number of posts to publish per account (1-10). Each account receives the same set of posts. Only applicable when nodeType="socialPublish".',
    ),
  intervalMinutes: z
    .number()
    .min(0)
    .max(1440)
    .optional()
    .describe(
      'Minutes between consecutive accounts across all platforms, starting with the first account immediately. For two accounts ten minutes apart set 10, and omit publishTargets[].delayMinutes unless the user separately requests an initial delay. Do not encode the same spacing in both fields. 0 = simultaneous. Only applicable when nodeType="socialPublish".',
    ),
  publishTargets: z
    .array(socialPublishTargetSchema)
    .min(1)
    .max(11)
    .optional()
    .describe(
      'Per-platform publish schedule for this single socialPublish node. One entry per platform; use it for scheduled/delayed publishing ("明天 9 点发" -> publishAt, "30 分钟后发" -> delayMinutes) or to publish to several platforms at once ("同时发 Facebook 和小红书" -> two entries). Accounts are still picked through accountVariableId; do not create separate publish tasks per platform. Only applicable when nodeType="socialPublish".',
    ),
});

// Variable value schema
export const workflowVariableValueSchema = z.object({
  type: z
    .enum(['text', 'resource'])
    .describe(
      'Value type: text for string and social account variables, resource for file uploads. A social account value is { "type": "text", "account": { "accountId", "platform" } }; there is no "account" type.',
    )
    .default('text'),
  text: z.string().optional().describe('Text value (for text type)'),
  resource: z
    .object({
      name: z.string().describe('Resource file name'),
      fileType: z.enum(['document', 'image', 'audio', 'video']).describe('Resource file type'),
      assetId: z.string().optional().describe('Media-library asset ID'),
      fileId: z.string().optional().describe('Legacy canvas DriveFile ID'),
      source: z.enum(['upload', 'media_library', 'canvas_file', 'upstream_output']).optional(),
    })
    .optional()
    .describe('Resource value (for resource type)'),
  account: z
    .object({
      accountId: z.string(),
      platform: z.string().optional(),
    })
    .optional()
    .describe('Stable social account value'),
});

// Value array schema - normalized format for Kimi compatibility
// LLM must output value in this standardized format: [{ type: 'text' | 'resource', text: string, resource?: object }]
const workflowVariableValueArraySchema = z.array(workflowVariableValueSchema);

// Variable schema for workflow plan
export const workflowVariableSchema = z.object({
  variableId: z.string().describe('Variable ID, unique and readonly'),
  variableType: z
    .enum(['string', 'option', 'resource'])
    .describe('Variable type: string for text input, resource for file upload')
    .default('string'),
  inputKind: z
    .enum(['text', 'media', 'social_account'])
    .optional()
    .describe('Stable typed-input semantic used by dedicated workflow nodes'),
  allowedPlatforms: z.array(z.string()).optional(),
  name: z.string().describe('Variable name used in the workflow'),
  description: z.string().describe('Description of what this variable represents'),
  required: z
    .boolean()
    .describe('Whether this variable is required. Defaults to false.')
    .default(false),
  resourceTypes: z
    .array(z.enum(['document', 'image', 'audio', 'video']))
    .optional()
    .describe('Accepted resource types (only for resource type variables)'),
  // ✅ Force value to be standardized array format - no union types
  // LLM must output: [{ "type": "text", "text": "value" }] instead of just "value"
  value: workflowVariableValueArraySchema
    .optional()
    .describe(
      'Variable values as array of typed objects. Each object must have "type" ("text" or "resource") and either "text" or "resource" field.',
    ),
});

export const workflowPlanSchema = z.object({
  title: z.string().describe('Title of the workflow plan'),
  tasks: z.array(workflowTaskSchema).describe('Array of workflow tasks to be executed'),
  variables: z
    .array(workflowVariableSchema)
    .describe('Array of variables defined for the workflow'),
});

// ============================================================================
// Semantic Patch Schema - Operations for granular workflow modifications
// ============================================================================

// Operation types enum
export const workflowPatchOpSchema = z.enum([
  'updateTitle',
  'createTask',
  'updateTask',
  'deleteTask',
  'createVariable',
  'updateVariable',
  'deleteVariable',
]);

export type WorkflowPatchOp = z.infer<typeof workflowPatchOpSchema>;

// Unified update data schema for both tasks and variables
export const workflowPatchDataSchema = z.object({
  // Task update fields
  title: z.string().optional().describe('New display title for the task'),
  prompt: z.string().optional().describe('New prompt or instruction for this task'),
  dependentTasks: z
    .array(z.string())
    .optional()
    .describe('New list of task IDs that must execute before this task'),
  toolsets: z.array(z.string()).optional().describe('New list of toolset IDs for this task'),

  // Variable update fields
  variableType: z.enum(['string', 'option', 'resource']).optional().describe('New variable type'),
  inputKind: z.enum(['text', 'media', 'social_account']).optional(),
  allowedPlatforms: z.array(z.string()).optional(),
  name: z.string().optional().describe('New variable name'),
  description: z.string().optional().describe('New variable description'),
  required: z.boolean().optional().describe('Whether this variable is required'),
  resourceTypes: z
    .array(z.enum(['document', 'image', 'audio', 'video']))
    .optional()
    .describe('New accepted resource types'),
  value: z.array(workflowVariableValueSchema).optional().describe('New variable values'),

  // Media task update fields
  mediaType: z.enum(['image', 'video']).optional().describe('New media type for the task'),
  batchCount: z.number().min(1).max(20).optional().describe('New batch count for media generation'),
  model: z.string().optional().describe('New model ID (media model or LLM)'),
  providerItemId: z
    .string()
    .optional()
    .describe('New provider item ID (media provider item or LLM provider item)'),
  visionProviderItemId: z.string().optional(),
  nodeType: z.enum(['productionStudio', 'socialPublish']).optional(),
  strategy: z
    .enum([
      'highlight-montage',
      'story-narration',
      'series-recommendation',
      'overlay-only',
      'assemble',
      'speech_cleanup',
      'highlight',
    ])
    .optional(),
  strategyMarket: z.enum(['zh-CN', 'en-US', 'fil-PH', 'id-ID', 'th-TH', 'vi-VN']).optional(),
  mediaVariableId: z.string().optional(),
  sourceVariableIds: z.array(z.string()).max(20).optional(),
  sourceNodeIds: z.array(z.string()).max(20).optional(),
  requestVariableId: z.string().optional(),
  targetPlatform: z.string().max(64).optional(),
  targetDurationSeconds: z.number().int().min(1).max(600).optional(),
  aspectRatio: z.enum(['9:16', '16:9', '1:1', '4:5']).optional(),
  resolution: z
    .object({
      width: z.number().int().min(16).max(7680),
      height: z.number().int().min(16).max(7680),
    })
    .strict()
    .optional(),
  includeSubtitles: z.boolean().optional(),
  musicAssetId: z.string().max(128).optional(),
  accountVariableId: z.string().optional(),
  platformType: z.number().int().min(1).max(20).optional(),
  postCount: z.number().int().min(1).max(10).optional(),
  intervalMinutes: z.number().min(0).max(1440).optional(),
  publishTargets: z.array(socialPublishTargetSchema).min(1).max(11).optional(),
  taskPrompts: z.record(z.string().max(10000)).optional(),
  taskSettings: z.record(publishTaskSettingsSchema).optional(),
  skillId: z.string().optional().describe('New workflow skill ID bound to this task'),
});

// Union of all patch operations - replaced with a single object for LLM compatibility
export const workflowPatchOperationSchema = z.object({
  op: workflowPatchOpSchema.describe('Operation type'),
  title: z.string().optional().describe('New workflow title (for updateTitle)'),
  taskId: z.string().optional().describe('ID of the task (for updateTask, deleteTask)'),
  task: workflowTaskSchema.optional().describe('Task definition (for createTask)'),
  variableId: z
    .string()
    .optional()
    .describe('ID of the variable (for updateVariable, deleteVariable)'),
  variable: workflowVariableSchema.optional().describe('Variable definition (for createVariable)'),
  data: workflowPatchDataSchema.optional().describe('Update data (for updateTask, updateVariable)'),
});

// Main patch schema with operations array
export const workflowPlanPatchSchema = z.object({
  planId: z
    .string()
    .optional()
    .describe(
      'The ID of the workflow plan to patch. If not provided, the latest version of the plan within this session will be chosen.',
    ),
  operations: z
    .array(workflowPatchOperationSchema)
    .describe('Array of operations to apply to the workflow plan (in order)'),
});

export type WorkflowTaskInput = z.infer<typeof workflowTaskSchema>;
export type WorkflowVariableValue = z.infer<typeof workflowVariableValueSchema>;
export type WorkflowVariableInput = z.infer<typeof workflowVariableSchema>;
export type WorkflowPatchOperation = z.infer<typeof workflowPatchOperationSchema>;
export type WorkflowPlanPatch = z.infer<typeof workflowPlanPatchSchema>;

// Result of applying patch operations
export type ApplyPatchResult = {
  success: boolean;
  data?: WorkflowPlan;
  error?: string;
};

/**
 * Apply semantic patch operations to a workflow plan
 * Operations are applied in order
 */
export const applyWorkflowPatchOperations = (
  currentPlan: WorkflowPlan,
  operations: WorkflowPatchOperation[],
): ApplyPatchResult => {
  // Create a mutable copy of the current plan
  const plan: WorkflowPlan = {
    title: currentPlan.title,
    tasks: [...(currentPlan.tasks ?? [])],
    variables: [...(currentPlan.variables ?? [])],
  };

  for (const operation of operations) {
    const { op, title, taskId, task, variableId, variable, data } = operation;

    switch (op) {
      case 'updateTitle': {
        if (title !== undefined) {
          plan.title = title;
        }
        break;
      }

      case 'createTask': {
        if (!task) break;
        // Validate task has required fields
        const validatedTask = workflowTaskSchema.safeParse(task);
        if (!validatedTask.success) {
          return {
            success: false,
            error: `Invalid task data: ${validatedTask.error.message}`,
          };
        }
        // Check if task ID already exists
        const existingTask = plan.tasks?.find((t) => t.id === validatedTask.data.id);
        if (existingTask) {
          return {
            success: false,
            error: `Task with ID "${validatedTask.data.id}" already exists. Use updateTask to modify existing tasks.`,
          };
        }
        plan.tasks!.push(validatedTask.data as WorkflowTask);
        break;
      }

      case 'updateTask': {
        if (!taskId) break;
        const taskIndex = plan.tasks?.findIndex((t) => t.id === taskId) ?? -1;
        if (taskIndex === -1) {
          return {
            success: false,
            error: `Task with ID "${taskId}" not found. Use createTask to create new tasks.`,
          };
        }
        const existingTask = plan.tasks![taskIndex];
        if (data) {
          plan.tasks![taskIndex] = {
            ...existingTask,
            ...(data.title !== undefined && { title: data.title }),
            ...(data.prompt !== undefined && { prompt: data.prompt }),
            ...(data.dependentTasks !== undefined && {
              dependentTasks: data.dependentTasks,
            }),
            ...(data.toolsets !== undefined && { toolsets: data.toolsets }),
            ...(data.mediaType !== undefined && { mediaType: data.mediaType }),
            ...(data.batchCount !== undefined && { batchCount: data.batchCount }),
            ...(data.model !== undefined && { model: data.model }),
            ...(data.providerItemId !== undefined && { providerItemId: data.providerItemId }),
            ...(data.visionProviderItemId !== undefined && {
              visionProviderItemId: data.visionProviderItemId,
            }),
            ...(data.nodeType !== undefined && { nodeType: data.nodeType }),
            ...(data.strategy !== undefined && { strategy: data.strategy }),
            ...(data.mediaVariableId !== undefined && {
              mediaVariableId: data.mediaVariableId,
            }),
            ...(data.requestVariableId !== undefined && {
              requestVariableId: data.requestVariableId,
            }),
            ...(data.accountVariableId !== undefined && {
              accountVariableId: data.accountVariableId,
            }),
            ...(data.platformType !== undefined && { platformType: data.platformType }),
            ...(data.postCount !== undefined && { postCount: data.postCount }),
            ...(data.intervalMinutes !== undefined && {
              intervalMinutes: data.intervalMinutes,
            }),
            ...(data.publishTargets !== undefined && { publishTargets: data.publishTargets }),
            ...(data.taskPrompts !== undefined && { taskPrompts: data.taskPrompts }),
            ...(data.taskSettings !== undefined && { taskSettings: data.taskSettings }),
            ...(data.skillId !== undefined && { skillId: data.skillId }),
          };
        }
        break;
      }

      case 'deleteTask': {
        if (!taskId) break;
        const taskIndex = plan.tasks?.findIndex((t) => t.id === taskId) ?? -1;
        if (taskIndex === -1) {
          return {
            success: false,
            error: `Task with ID "${taskId}" not found.`,
          };
        }
        plan.tasks = plan.tasks?.filter((t) => t.id !== taskId) ?? [];
        // Also remove references to this task from dependentTasks
        plan.tasks = plan.tasks.map((t) => ({
          ...t,
          dependentTasks: t.dependentTasks?.filter((depId) => depId !== taskId),
        }));
        break;
      }

      case 'createVariable': {
        if (!variable) break;
        // Validate variable has required fields
        const validatedVariable = workflowVariableSchema.safeParse(variable);
        if (!validatedVariable.success) {
          return {
            success: false,
            error: `Invalid variable data: ${validatedVariable.error.message}`,
          };
        }
        // Check if variable ID already exists
        const existingVar = plan.variables?.find(
          (v) => v.variableId === validatedVariable.data.variableId,
        );
        if (existingVar) {
          return {
            success: false,
            error: `Variable with ID "${validatedVariable.data.variableId}" already exists. Use updateVariable to modify existing variables.`,
          };
        }
        plan.variables!.push(validatedVariable.data as WorkflowVariable);
        break;
      }

      case 'updateVariable': {
        if (!variableId) break;
        const varIndex = plan.variables?.findIndex((v) => v.variableId === variableId) ?? -1;
        if (varIndex === -1) {
          return {
            success: false,
            error: `Variable with ID "${variableId}" not found. Use createVariable to create new variables.`,
          };
        }
        const existingVar = plan.variables![varIndex];
        if (data) {
          const updatedVar = {
            ...existingVar,
            ...(data.variableType !== undefined && {
              variableType: data.variableType,
            }),
            ...(data.inputKind !== undefined && { inputKind: data.inputKind }),
            ...(data.allowedPlatforms !== undefined && {
              allowedPlatforms: data.allowedPlatforms,
            }),
            ...(data.name !== undefined && { name: data.name }),
            ...(data.description !== undefined && {
              description: data.description,
            }),
            ...(data.required !== undefined && { required: data.required }),
            ...(data.resourceTypes !== undefined && {
              resourceTypes: data.resourceTypes,
            }),
            ...(data.value !== undefined && { value: data.value }),
          };
          // Validate the updated variable
          const validatedVariable = workflowVariableSchema.safeParse(updatedVar);
          if (!validatedVariable.success) {
            return {
              success: false,
              error: `Invalid variable update: ${validatedVariable.error.message}`,
            };
          }
          plan.variables![varIndex] = validatedVariable.data as WorkflowVariable;
        }
        break;
      }

      case 'deleteVariable': {
        if (!variableId) break;
        const varIndex = plan.variables?.findIndex((v) => v.variableId === variableId) ?? -1;
        if (varIndex === -1) {
          return {
            success: false,
            error: `Variable with ID "${variableId}" not found.`,
          };
        }
        plan.variables = plan.variables?.filter((v) => v.variableId !== variableId) ?? [];
        break;
      }

      default: {
        // Type safety check - should never reach here
        const _exhaustiveCheck: never = op;
        return {
          success: false,
          error: `Unknown operation type: ${_exhaustiveCheck as string}`,
        };
      }
    }
  }

  return { success: true, data: plan };
};

/**
 * Parse and validate workflow plan patch input
 */
export const parseWorkflowPlanPatch = (
  data: unknown,
): { success: boolean; data?: WorkflowPlanPatch; error?: string } => {
  const result = workflowPlanPatchSchema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  }

  const errorMessages: string[] = [];
  for (const issue of result.error.issues) {
    const path = issue.path.length > 0 ? issue.path.join('.') : 'root';
    errorMessages.push(`[${path}]: ${issue.message}`);
  }

  return {
    success: false,
    error: `Workflow plan patch validation failed:\n${errorMessages.join('\n')}`,
  };
};

// Enhanced parsing function with detailed error reporting
export type ParseWorkflowPlanResult = {
  success: boolean;
  data?: WorkflowPlan;
  error?: string;
};

export interface DedicatedWorkflowContractOptions {
  /**
   * 发布能力归 socialPublish 节点的平台 toolset id。
   *
   * 这些 toolset 走的是平台 OAuth 应用授权，和 socialPublish 节点使用的
   * 「用户已绑定账号 + Cookie 浏览器」是两条不同的链路。同一个平台上只能有
   * 一个发布实现，否则模型会在两者之间摇摆——实测同一句需求换个说法就会
   * 从 socialPublish 倒向 toolset。因此把归属写成契约，由校验兜底，
   * 不依赖提示词里的措辞。
   */
  publishOwnedToolsetIds?: string[];

  /**
   * socialPublish 节点缺省使用的视觉模型 providerItemId。
   *
   * 这个字段是系统能力，不是设计决策：能用哪些视觉模型由用户的供应商配置决定，
   * 模型只是从清单里抄一个 ID。实测让它手抄是首要失败源——抄漏就被契约打回，
   * 打回后它也猜不出该填什么，于是反复重试。这里由调用方给出默认值，
   * 模型没写就自动补上；系统一个视觉模型都没有时才报错，那时的提示才可执行。
   */
  defaultVisionProviderItemId?: string;

  /**
   * 当前账号可用的视觉模型 providerItemId 全集。
   *
   * 传入时会校验 socialPublish 引用的模型是否真实存在：模型被契约打回后
   * 会开始编造 ID（实测出现过 `anthropic.claude-3-5-sonnet-20241022-v2:0`），
   * 编造值不为空，仅靠默认值补全拦不住，工作流要到执行时才失败。
   */
  availableVisionProviderItemIds?: string[];
}

export const validateDedicatedWorkflowContracts = (
  plan: WorkflowPlan,
  options?: DedicatedWorkflowContractOptions,
): string[] => {
  const errors: string[] = [];
  const publishOwnedToolsetIds = new Set(options?.publishOwnedToolsetIds ?? []);
  if (publishOwnedToolsetIds.size > 0) {
    for (const task of plan.tasks ?? []) {
      const owned = (task.toolsets ?? []).filter((id) => publishOwnedToolsetIds.has(id));
      if (owned.length === 0) continue;
      errors.push(
        `任务「${task.title}」绑定了社媒平台 toolset（${owned.join(', ')}）。该平台的发布能力由 socialPublish 节点独占：请把这个任务改成 nodeType="socialPublish"，toolsets 置空，并设置 platformType、accountVariableId 和 visionProviderItemId。`,
      );
    }
  }
  const variablesById = new Map<string, WorkflowVariable>();
  for (const variable of plan.variables ?? []) {
    if (variablesById.has(variable.variableId)) {
      errors.push(`工作流变量 ID 重复: ${variable.variableId}`);
      continue;
    }
    variablesById.set(variable.variableId, variable);

    const normalizedType = getWorkflowVariableType(variable);
    if (variable.inputKind === 'media' && normalizedType !== 'resource') {
      errors.push(`素材变量「${variable.name}」必须为 resource 类型`);
    }
    if (variable.inputKind === 'social_account' && normalizedType !== 'option') {
      errors.push(`账号变量「${variable.name}」必须为 option 类型`);
    }
  }

  const taskIds = new Set<string>();
  for (const task of plan.tasks ?? []) {
    if (taskIds.has(task.id)) errors.push(`工作流任务 ID 重复: ${task.id}`);
    taskIds.add(task.id);
  }

  for (const task of plan.tasks ?? []) {
    const dedicatedTask = task as WorkflowTaskInput;
    const nodeType = dedicatedTask.nodeType;

    for (const dependencyId of dedicatedTask.dependentTasks ?? []) {
      if (dependencyId === dedicatedTask.id) {
        errors.push(`任务「${task.title}」不能依赖自身`);
      } else if (!taskIds.has(dependencyId)) {
        errors.push(`任务「${task.title}」依赖了不存在的任务: ${dependencyId}`);
      }
    }

    if (nodeType && dedicatedTask.mediaType) {
      errors.push(`专用节点「${task.title}」不能同时设置 mediaType`);
    }
    if (nodeType && (dedicatedTask.toolsets ?? []).length > 0) {
      errors.push(`专用节点「${task.title}」必须使用空 toolsets，不能绑定 MCP 或普通工具`);
    }

    if (nodeType === 'productionStudio') {
      const mediaVariableIds = [
        ...(dedicatedTask.sourceVariableIds ?? []),
        ...(dedicatedTask.mediaVariableId ? [dedicatedTask.mediaVariableId] : []),
      ];
      if (!mediaVariableIds.length && !(dedicatedTask.sourceNodeIds ?? []).length) {
        errors.push(`Production Studio 节点「${task.title}」必须绑定素材变量或上游媒体任务`);
      }
      for (const variableId of new Set(mediaVariableIds)) {
        const mediaVariable = variablesById.get(variableId);
        if (
          !mediaVariable ||
          mediaVariable.inputKind !== 'media' ||
          getWorkflowVariableType(mediaVariable) !== 'resource'
        ) {
          errors.push(
            `Production Studio 节点「${task.title}」的素材变量 ${variableId} 必须是 media/resource`,
          );
        }
      }
      for (const sourceNodeId of dedicatedTask.sourceNodeIds ?? []) {
        if (!taskIds.has(sourceNodeId)) {
          errors.push(
            `Production Studio 节点「${task.title}」引用了不存在的上游任务: ${sourceNodeId}`,
          );
        } else if (!(dedicatedTask.dependentTasks ?? []).includes(sourceNodeId)) {
          errors.push(
            `Production Studio 节点「${task.title}」的素材上游 ${sourceNodeId} 必须同时是依赖任务`,
          );
        }
      }
      const requestVariable = dedicatedTask.requestVariableId
        ? variablesById.get(dedicatedTask.requestVariableId)
        : undefined;
      if (
        dedicatedTask.requestVariableId &&
        (!requestVariable ||
          requestVariable.inputKind !== 'text' ||
          getWorkflowVariableType(requestVariable) !== 'string')
      ) {
        errors.push(`Production Studio 节点「${task.title}」的 requestVariableId 必须引用文本变量`);
      }
      if (dedicatedTask.accountVariableId) {
        errors.push(`Production Studio 节点「${task.title}」不得绑定账号变量`);
      }
      if (dedicatedTask.visionProviderItemId || dedicatedTask.platformType) {
        errors.push(`Production Studio 节点「${task.title}」不得设置发布模型或平台字段`);
      }
    }

    if (nodeType === 'socialPublish') {
      // 这个节点的问题必须一次性讲完，不能逐条报。实测逐条报错时模型每轮只修一个，
      // 修 A 的同时把 B 改坏（删掉多余字段的那一版顺手把 accountVariableId 也删了），
      // 于是在重试上限内始终收敛不了。这里收集全部问题，连同正确形状一起给出。
      const problems: string[] = [];

      const accountVariable = dedicatedTask.accountVariableId
        ? variablesById.get(dedicatedTask.accountVariableId)
        : undefined;
      if (
        !accountVariable ||
        accountVariable.inputKind !== 'social_account' ||
        getWorkflowVariableType(accountVariable) !== 'option'
      ) {
        problems.push(
          'accountVariableId 必须指向一个 variableType="option" 且 inputKind="social_account" 的变量；该变量要一并写进 variables',
        );
      }
      if (!dedicatedTask.visionProviderItemId) {
        problems.push(
          'visionProviderItemId 为空，且当前账号没有可用的视觉模型——请先在「设置 - 模型配置」里启用一个支持视觉的模型再生成工作流',
        );
      }
      const platformType = Number(dedicatedTask.platformType);
      if (!Number.isInteger(platformType) || platformType < 1 || platformType > 11) {
        problems.push('platformType 必须是 1-11 之间的平台编号');
      }
      // 素材可以来自用户上传的素材变量，也可以来自上游媒体任务，两者都合法。
      // 用户自带图片直接发布是最常见的发布场景之一，之前一律拒绝时，模型会
      // 反复换字段重试（mediaVariableId → sourceVariableIds），始终收敛不了。
      const mediaVariableId = dedicatedTask.mediaVariableId;
      if (mediaVariableId) {
        const mediaVariable = variablesById.get(mediaVariableId);
        if (
          !mediaVariable ||
          mediaVariable.inputKind !== 'media' ||
          getWorkflowVariableType(mediaVariable) !== 'resource'
        ) {
          problems.push(
            'mediaVariableId 必须指向一个 variableType="resource" 且 inputKind="media" 的素材变量；该变量要一并写进 variables',
          );
        }
      }
      const hasUpstreamProducer = (task.dependentTasks ?? []).length > 0;
      if (
        !mediaVariableId &&
        !hasUpstreamProducer &&
        !(dedicatedTask.publishTargets ?? []).some((target: any) => target.mediaVariableId)
      ) {
        problems.push(
          '没有任何发布素材来源：要么用 mediaVariableId 绑定一个用户上传的素材变量，要么通过 dependentTasks 依赖一个产出图片/视频的上游任务',
        );
      }
      // 发布文案同样可以来自用户输入。素材开了口子却不开文案时，「发我这张图配这段话」
      // 只能表达一半，模型会连续四轮坚持写 requestVariableId 直到重试耗尽。
      const requestVariableId = dedicatedTask.requestVariableId;
      if (requestVariableId) {
        const requestVariable = variablesById.get(requestVariableId);
        if (
          !requestVariable ||
          requestVariable.inputKind !== 'text' ||
          getWorkflowVariableType(requestVariable) !== 'string'
        ) {
          problems.push(
            'requestVariableId 必须指向一个 variableType="string" 且 inputKind="text" 的文案变量；该变量要一并写进 variables',
          );
        }
      }
      for (const [index, target] of (dedicatedTask.publishTargets ?? []).entries()) {
        const label = `publishTargets[${index}]`;
        for (const [field, kind, type] of [
          ['mediaVariableId', 'media', 'resource'],
          ['requestVariableId', 'text', 'string'],
        ] as const) {
          const id = (target as any)[field];
          const variable = id ? variablesById.get(id) : undefined;
          if (
            id &&
            (!variable || variable.inputKind !== kind || getWorkflowVariableType(variable) !== type)
          ) {
            problems.push(`${label}.${field} 必须引用已定义的 ${type} 变量`);
          }
        }
        if (
          !Number.isInteger(target.platformType) ||
          target.platformType < 1 ||
          target.platformType > 11
        ) {
          problems.push(`${label}.platformType 必须是 1-11 之间的平台编号`);
        }
        if (target.publishAt !== undefined && !Number.isFinite(Date.parse(target.publishAt))) {
          problems.push(
            `${label}.publishAt「${target.publishAt}」无法解析，请写带时区的 ISO 时间，如 2026-09-12T09:00:00+08:00`,
          );
        }
        if (
          target.delayMinutes !== undefined &&
          (!Number.isFinite(target.delayMinutes) ||
            target.delayMinutes < 0 ||
            target.delayMinutes > 1440)
        ) {
          problems.push(`${label}.delayMinutes 必须在 0 到 1440 之间`);
        }
      }
      const targetPlatforms = (dedicatedTask.publishTargets ?? []).map(
        (target) => target.platformType,
      );
      if (new Set(targetPlatforms).size !== targetPlatforms.length) {
        problems.push('publishTargets 里同一个平台只能出现一次');
      }

      // 其余 Production Studio 专用字段仍然禁止，且必须点名——只说「不得设置专用字段」时
      // 模型不知道该删哪一个，重试往往原样再发一遍。
      const productionStudioOnlyFields = (
        ['strategy', 'sourceVariableIds', 'sourceNodeIds'] as const
      ).filter((field) => dedicatedTask[field] !== undefined);
      if (productionStudioOnlyFields.length > 0) {
        problems.push(
          `请删除 Production Studio 专用字段 ${productionStudioOnlyFields.join('、')}。用户上传的素材走 mediaVariableId，上游产出的素材走 dependentTasks，没有第三种传法`,
        );
      }

      if (problems.length > 0) {
        errors.push(
          `社媒发布节点「${task.title}」不合规，请一次性改完以下全部问题（只修其中一条会再次被打回）：\n${problems
            .map((problem, index) => `  ${index + 1}. ${problem}`)
            .join(
              '\n',
            )}\n  正确形状：{ nodeType: "socialPublish", platformType: <平台编号>, accountVariableId: <账号变量 ID>, toolsets: [], 素材来源二选一（mediaVariableId: <素材变量 ID> 用于用户上传，dependentTasks: [<上游任务 ID>] 用于工作流内生成），用户自带文案时再加 requestVariableId: <文案变量 ID>，定时 / 多平台时再加 publishTargets、账号间隔再加 intervalMinutes }，其余专用字段一律不填。`,
        );
      }
    }

    if (!nodeType && !dedicatedTask.mediaType) {
      const specialFields = [
        dedicatedTask.visionProviderItemId,
        dedicatedTask.accountVariableId,
        dedicatedTask.platformType,
        dedicatedTask.postCount,
        dedicatedTask.intervalMinutes,
        dedicatedTask.publishTargets,
        dedicatedTask.strategy,
        dedicatedTask.mediaVariableId,
        dedicatedTask.sourceVariableIds,
        dedicatedTask.sourceNodeIds,
        dedicatedTask.requestVariableId,
        dedicatedTask.targetPlatform,
        dedicatedTask.targetDurationSeconds,
        dedicatedTask.aspectRatio,
        dedicatedTask.resolution,
        dedicatedTask.includeSubtitles,
        dedicatedTask.musicAssetId,
      ];
      if (specialFields.some((value) => value !== undefined)) {
        errors.push(`普通任务「${task.title}」不得设置媒体、Production Studio 或发布专用字段`);
      }
    }
  }

  const dependencies = new Map(
    (plan.tasks ?? []).map((task) => [
      task.id,
      (task.dependentTasks ?? []).filter((id) => taskIds.has(id)),
    ]),
  );
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const hasCycle = (taskId: string): boolean => {
    if (visiting.has(taskId)) return true;
    if (visited.has(taskId)) return false;
    visiting.add(taskId);
    for (const dependencyId of dependencies.get(taskId) ?? []) {
      if (hasCycle(dependencyId)) return true;
    }
    visiting.delete(taskId);
    visited.add(taskId);
    return false;
  };
  if ([...dependencies.keys()].some(hasCycle)) {
    errors.push('任务依赖关系存在环，必须构成无环 DAG');
  }

  return errors;
};

/**
 * 补齐由系统而非模型决定的 socialPublish 字段。
 *
 * 只补模型没写的字段：模型显式选了某个视觉模型时以它为准。
 */
export const applyDedicatedWorkflowDefaults = (
  plan: WorkflowPlan,
  options?: DedicatedWorkflowContractOptions,
): WorkflowPlan => {
  const defaultVisionProviderItemId = options?.defaultVisionProviderItemId;
  if (!defaultVisionProviderItemId || !Array.isArray(plan.tasks)) return plan;
  const availableIds = new Set(options?.availableVisionProviderItemIds ?? []);

  let changed = false;
  const tasks = plan.tasks.map((task) => {
    const dedicatedTask = task as WorkflowTaskInput;
    if (dedicatedTask.nodeType !== 'socialPublish') return task;
    const chosen = dedicatedTask.visionProviderItemId;
    // 没写就补默认值；写了但不在可用清单里（编造的 ID）同样换成默认值。
    // 清单未传时保持旧行为，只补空值。
    const valid = chosen && (availableIds.size === 0 || availableIds.has(chosen));
    if (valid) return task;
    changed = true;
    return { ...task, visionProviderItemId: defaultVisionProviderItemId };
  });

  return changed ? { ...plan, tasks } : plan;
};

export const parseWorkflowPlan = (
  data: unknown,
  options?: DedicatedWorkflowContractOptions,
): ParseWorkflowPlanResult => {
  const result = workflowPlanSchema.safeParse(data);

  if (result.success) {
    const plan = applyDedicatedWorkflowDefaults(result.data as WorkflowPlan, options);
    const contractErrors = validateDedicatedWorkflowContracts(plan, options);
    if (contractErrors.length > 0) {
      return {
        success: false,
        error: `Dedicated workflow contract validation failed:\n${contractErrors.join('\n')}`,
      };
    }
    return { success: true, data: plan };
  }

  // Collect detailed error messages
  const errorMessages: string[] = [];

  for (const issue of result.error.issues) {
    const path = issue.path.length > 0 ? issue.path.join('.') : 'root';
    errorMessages.push(`[${path}]: ${issue.message}`);
  }

  return {
    success: false,
    error: `Workflow plan validation failed:\n${errorMessages.join('\n')}`,
  };
};

export const normalizeWorkflowPlan = (plan: WorkflowPlan): WorkflowPlan => {
  return {
    ...plan,
    tasks:
      plan.tasks?.map((task) => {
        // Ensure toolsets array exists
        const toolsets = Array.isArray(task.toolsets) ? [...task.toolsets] : [];

        return {
          ...task,
          toolsets,
        };
      }) ?? [],
  };
};

export const planVariableToWorkflowVariable = (
  planVariable: WorkflowVariable,
): WorkflowVariable => {
  // LLM now outputs standardized array format: [{ type: 'text' | 'resource', text?: string, resource?: object }]
  // Just validate and normalize the value array
  const normalizedValue = Array.isArray(planVariable.value) ? planVariable.value : [];

  return {
    variableId: planVariable.variableId,
    variableType: getWorkflowVariableType(planVariable),
    name: planVariable.name,
    value: normalizedValue.map((value) => ({
      type: value?.type as 'text' | 'resource',
      text: value?.text,
      // Only include resource if it has the required fields
      ...(value?.resource?.name && value?.resource?.fileType
        ? {
            resource: {
              name: value.resource.name,
              fileType: value.resource.fileType,
              assetId: value.resource.assetId,
              fileId: value.resource.fileId,
              source: value.resource.source,
            },
          }
        : {}),
      ...(value?.account?.accountId ? { account: value.account } : {}),
    })),
    description: planVariable.description,
    required: planVariable.required ?? false,
    resourceTypes: planVariable.resourceTypes,
    inputKind: planVariable.inputKind,
    allowedPlatforms: planVariable.allowedPlatforms,
  };
};

/**
 * inputKind is the stable business semantic. Keep variableType as the legacy
 * rendering shape, but never let an inconsistent generated value move media or
 * social accounts into the text section.
 */
export const getWorkflowVariableType = (
  variable: Pick<WorkflowVariable, 'inputKind' | 'variableType'>,
): 'string' | 'option' | 'resource' => {
  if (variable.inputKind === 'media') return 'resource';
  if (variable.inputKind === 'social_account') return 'option';
  if (variable.inputKind === 'text') return 'string';
  return variable.variableType ?? 'string';
};

// Generate canvas data from workflow plan
// Convert validated plan tasks into first-class canvas nodes and dependency edges.
export const generateCanvasDataFromWorkflowPlan = (
  workflowPlan: WorkflowPlan,
  toolsets: GenericToolset[],
  options?: {
    autoLayout?: boolean;
    defaultModel?: ModelInfo;
    startNodes?: CanvasNode[];
    // Available LLM models; used to honor per-task providerItemId chosen by the planner
    // instead of forcing the default model onto every generated node.
    llmModels?: ModelInfo[];
    // Available media generation models; used to resolve the real ModelInfo (label/provider)
    // for image/video tasks by providerItemId so the node UI displays the chosen model.
    mediaModels?: ModelInfo[];
    // Available workflow skills; tasks referencing skillId get the skill bound to the node.
    workflowSkills?: Array<{ skillId: string; name: string; description?: string }>;
    // Available prompt skills; tasks referencing promptSkillIds get the skills bound to the node.
    promptSkills?: Array<{ skillId: string; title: string; description?: string }>;
  },
): RawCanvasData => {
  const nodes: RawCanvasData['nodes'] = [];
  const edges: RawCanvasData['edges'] = [];

  // Maps to resolve context references
  const taskIdToNodeId = new Map<string, string>();
  const taskIdToEntityId = new Map<string, string>();

  const {
    autoLayout = false,
    defaultModel,
    startNodes = [],
    llmModels,
    mediaModels,
    workflowSkills,
    promptSkills,
  } = options ?? {};

  // Simple layout positions for non-auto-layout mode
  const taskStartX = 0;
  const rowStepY = 240;

  if (Array.isArray(workflowPlan.tasks) && workflowPlan.tasks.length > 0) {
    // Phase 1: Process tasks in dependency order
    // First, identify tasks with no dependencies (roots)
    const taskMap = new Map<string, (typeof workflowPlan.tasks)[0]>();
    const dependencyGraph = new Map<string, Set<string>>();

    for (const task of workflowPlan.tasks) {
      const taskId = task?.id ?? `task-${genUniqueId()}`;
      taskMap.set(taskId, task);

      if (Array.isArray(task.dependentTasks)) {
        for (const depTaskId of task.dependentTasks) {
          if (!dependencyGraph.has(taskId)) {
            dependencyGraph.set(taskId, new Set());
          }
          dependencyGraph.get(taskId)!.add(depTaskId);
        }
      }
    }

    // Find tasks with no dependencies (roots)
    const rootTasks: typeof workflowPlan.tasks = [];
    const dependentTasks: typeof workflowPlan.tasks = [];

    for (const task of workflowPlan.tasks) {
      const taskId = task?.id ?? `task-${genUniqueId()}`;
      const hasDependencies = dependencyGraph.has(taskId) && dependencyGraph.get(taskId)!.size > 0;

      if (!hasDependencies) {
        rootTasks.push(task);
      } else {
        dependentTasks.push(task);
      }
    }

    // Process root tasks first
    let taskIndex = 0;
    for (const task of [...rootTasks, ...dependentTasks]) {
      const taskId = task?.id ?? `task-${genUniqueId()}`;
      const taskTitle = task?.title ?? '';
      const taskPrompt = task?.prompt ?? '';
      const taskMediaType = task?.mediaType as 'image' | 'video' | undefined;
      const taskBatchCount = task?.batchCount ?? 1;
      const taskModel = task?.model as string | undefined;
      const taskProviderItemId = task?.providerItemId as string | undefined;
      const taskSkillId = (task as any)?.skillId as string | undefined;
      const taskPromptSkillIds = Array.isArray((task as any)?.promptSkillIds)
        ? ((task as any).promptSkillIds as string[])
        : [];
      const taskProductionStudio = (task as any)?.nodeType === 'productionStudio';
      // socialPublish task: emit a socialPublish canvas node. Material upload
      // and account selection happen in the node UI; here we only carry the
      // platform / interval / postCount decisions made by the LLM.
      const taskSocialPublish = (task as any)?.nodeType === 'socialPublish';
      const taskPlatformType = Number((task as any)?.platformType) || 1;
      const taskPostCount = Math.max(1, Math.min(10, Number((task as any)?.postCount) || 1));
      const taskIntervalMinutes = Math.max(
        0,
        Math.min(1440, Number((task as any)?.intervalMinutes) || 0),
      );
      const taskStrategy = (task as any)?.strategy ?? 'highlight-montage';
      const taskMediaVariableId = (task as any)?.mediaVariableId;
      const taskSourceVariableIds = (task as any)?.sourceVariableIds;
      const taskSourceNodeIds = (task as any)?.sourceNodeIds;
      const taskRequestVariableId = (task as any)?.requestVariableId;
      const taskAccountVariableId = (task as any)?.accountVariableId;
      const taskPublishTargets = (task as any)?.publishTargets as
        | z.infer<typeof socialPublishTargetSchema>[]
        | undefined;
      const taskVisionProviderItemId = (task as any)?.visionProviderItemId as string | undefined;

      // Build selected toolsets metadata from task toolset ids
      const selectedToolsets: GenericToolset[] = [];
      if (Array.isArray(task.toolsets)) {
        for (const toolsetId of task.toolsets) {
          // Find the corresponding toolset from the available toolsets
          const toolset =
            toolsets?.find((t) => t.id === toolsetId) ||
            toolsets?.find((t) => t.toolset?.key === toolsetId);
          if (toolset) {
            selectedToolsets.push(toolset);
          }
        }
      }

      // Create connection filters for dependent tasks
      const connectTo: CanvasNodeFilter[] = [];
      if (Array.isArray(task.dependentTasks)) {
        for (const dependentTaskId of task.dependentTasks) {
          const dependentEntityId = taskIdToEntityId.get(dependentTaskId);
          if (dependentEntityId) {
            connectTo.push({
              type: 'skillResponse',
              entityId: dependentEntityId,
              handleType: 'source',
            });
          }
        }
      }

      // socialPublish task: create a socialPublish canvas node. The node is a
      // barrier — downstream tasks (via dependentTasks) only fire after every
      // selected account × every post has finished publishing. We seed the
      // metadata with the LLM-decided platform / interval / postCount so the
      // user only needs to pick accounts and upload material in the node UI.
      if (taskProductionStudio) {
        const productionStudioNodeType = 'productionStudio' as const;
        const taskEntityId = genNodeEntityId(productionStudioNodeType);
        const defaultPosition = autoLayout ? undefined : { x: taskStartX, y: taskIndex * rowStepY };
        taskIndex++;
        const { newNode } = prepareAddNode({
          node: {
            type: productionStudioNodeType,
            position: defaultPosition,
            data: {
              title: taskTitle,
              editedTitle: taskTitle,
              entityId: taskEntityId,
              contentPreview: '',
              metadata: {
                strategy: taskStrategy,
                strategyMarket: (task as any)?.strategyMarket,
                request: taskPrompt,
                mediaVariableId: taskMediaVariableId,
                sourceVariableIds: taskSourceVariableIds,
                sourceNodeIds: (taskSourceNodeIds ?? [])
                  .map((sourceTaskId: string) => taskIdToNodeId.get(sourceTaskId))
                  .filter(Boolean),
                requestVariableId: taskRequestVariableId,
                targetPlatform: (task as any)?.targetPlatform,
                targetDurationSeconds: (task as any)?.targetDurationSeconds,
                aspectRatio: (task as any)?.aspectRatio,
                resolution: (task as any)?.resolution,
                includeSubtitles: (task as any)?.includeSubtitles,
                musicAssetId: (task as any)?.musicAssetId,
                providerItemId: taskProviderItemId,
                modelInfo: taskProviderItemId
                  ? llmModels?.find((model) => model.providerItemId === taskProviderItemId)
                  : undefined,
                status: 'init',
              },
            },
          },
          nodes: [...startNodes, ...nodes] as any[],
          edges: edges as any[],
          connectTo,
          autoLayout,
        });
        nodes.push(newNode);
        taskIdToNodeId.set(taskId, newNode.id);
        taskIdToEntityId.set(taskId, taskEntityId);
      } else if (taskSocialPublish) {
        const socialPublishNodeType = 'socialPublish' as CanvasNode['type'];
        const taskEntityId = genNodeEntityId(socialPublishNodeType);

        const defaultPosition = autoLayout
          ? undefined
          : {
              x: taskStartX,
              y: taskIndex * rowStepY,
            };

        taskIndex++;

        // Pre-seed `posts` with `taskPostCount` empty drafts so the node UI
        // opens with the right number of post editors ready to fill in.
        const posts = Array.from({ length: taskPostCount }, () => ({
          title: '',
          text: '',
          tags: '',
          fileList: [] as string[],
          fileNames: [] as string[],
          fileType: 2,
        }));

        const nodeData: Partial<CanvasNode> = {
          type: socialPublishNodeType,
          position: defaultPosition,
          data: {
            title: taskTitle,
            editedTitle: taskTitle,
            entityId: taskEntityId,
            contentPreview: '',
            metadata: {
              platformType: taskPlatformType,
              posts,
              intervalMinutes: taskIntervalMinutes,
              ...((task as any).taskPrompts ? { taskPrompts: (task as any).taskPrompts } : {}),
              ...((task as any).taskSettings ? { taskSettings: (task as any).taskSettings } : {}),
              ...(taskPublishTargets?.length
                ? {
                    publishTargets: taskPublishTargets.map((target) => ({
                      intervalMinutes: taskIntervalMinutes,
                      mediaVariableId: taskMediaVariableId ?? '',
                      requestVariableId: taskRequestVariableId ?? '',
                      ...target,
                      userPrompt: target.userPrompt ?? task.prompt,
                    })),
                  }
                : {
                    publishTargets: [
                      {
                        platformType: taskPlatformType,
                        userPrompt: taskPrompt,
                        intervalMinutes: taskIntervalMinutes,
                        mediaVariableId: taskMediaVariableId ?? '',
                        requestVariableId: taskRequestVariableId ?? '',
                      },
                    ],
                  }),
              prompt: taskPrompt,
              // auto：账号登录态有效时走本机 Cookie 浏览器（节点脚本重放、断点续跑都在
              // 这条通道上），无效时才转云浏览器。默认 cloud 时发布成功全靠云浏览器
              // 失败后的回退；云浏览器一旦可用，又只返回「执行中」而不核验发布结果。
              preferredChannel: 'auto',
              publishStatus: 'init',
              accountVariableId: taskAccountVariableId,
              // 用户上传的素材变量：执行时由发布节点直接取值，
              // 与依赖上游媒体节点是两条并列的素材来源。
              mediaVariableId: taskMediaVariableId,
              // 用户自带的发布文案变量；留空时由节点根据素材生成。
              requestVariableId: taskRequestVariableId,
              visionProviderItemId: taskVisionProviderItemId,
              modelInfo: taskVisionProviderItemId
                ? llmModels?.find((model) => model.providerItemId === taskVisionProviderItemId)
                : undefined,
            },
          },
        };

        const { newNode } = prepareAddNode({
          node: nodeData,
          nodes: [...startNodes, ...nodes] as any[],
          edges: edges as any[],
          connectTo,
          autoLayout,
        });

        nodes.push(newNode);
        taskIdToNodeId.set(taskId, newNode.id);
        taskIdToEntityId.set(taskId, taskEntityId);
      } else if (taskMediaType && taskBatchCount > 0) {
        // Media generation task: create image/video nodes
        const batchNodeIds: string[] = [];
        const batchEntityIds: string[] = [];

        // Build modelInfo from the real media model list (matched by providerItemId) so
        // downstream components (node model selector, retry, preview) display the chosen
        // model with its proper label; fall back to a stub built from the raw model string.
        const matchedMediaModel = taskProviderItemId
          ? mediaModels?.find((m) => m.providerItemId === taskProviderItemId)
          : undefined;
        const mediaModelInfo: ModelInfo | undefined = matchedMediaModel
          ? matchedMediaModel
          : taskModel
            ? {
                name: taskModel,
                label: taskModel,
                provider: '',
                providerItemId: taskProviderItemId ?? '',
                contextLimit: 0,
                maxOutput: 0,
              }
            : undefined;

        for (let batchIdx = 0; batchIdx < taskBatchCount; batchIdx++) {
          const taskEntityId = genNodeEntityId(taskMediaType);
          const batchTitle =
            taskBatchCount > 1 ? `${taskTitle} (${batchIdx + 1}/${taskBatchCount})` : taskTitle;

          const defaultPosition = autoLayout
            ? undefined
            : {
                x: taskStartX,
                y: (taskIndex + batchIdx) * rowStepY,
              };

          const mediaNodeData: Partial<CanvasNode> = {
            type: taskMediaType,
            position: defaultPosition,
            data: {
              title: batchTitle,
              editedTitle: batchTitle,
              entityId: taskEntityId,
              contentPreview: '',
              metadata: {
                prompt: taskPrompt,
                ...(taskPromptSkillIds.length
                  ? { promptSkills: taskPromptSkillIds.map((skillId) => ({ skillId })) }
                  : {}),
                model: taskModel ?? matchedMediaModel?.name ?? '',
                providerItemId: taskProviderItemId ?? matchedMediaModel?.providerItemId ?? '',
                status: 'init',
                // Include modelInfo object so downstream components can read the model
                ...(mediaModelInfo ? { modelInfo: mediaModelInfo } : {}),
                ...(taskMediaType === 'image' ? { imageUrl: '', storageKey: '' } : {}),
                ...(taskMediaType === 'video' ? { videoUrl: '', storageKey: '' } : {}),
              },
            },
          };

          const { newNode } = prepareAddNode({
            node: mediaNodeData,
            nodes: [...startNodes, ...nodes] as any[],
            edges: edges as any[],
            connectTo,
            autoLayout,
          });

          nodes.push(newNode);
          batchNodeIds.push(newNode.id);
          batchEntityIds.push(taskEntityId);
        }

        // Map the first batch node as the primary node for this task
        // (for dependency resolution from downstream tasks)
        taskIdToNodeId.set(taskId, batchNodeIds[0]);
        taskIdToEntityId.set(taskId, batchEntityIds[0]);

        // Also map all batch node IDs for edge creation
        for (const batchNodeId of batchNodeIds) {
          taskIdToNodeId.set(`${taskId}__batch_${batchNodeId}`, batchNodeId);
        }

        taskIndex += taskBatchCount;
      } else {
        // Regular skillResponse task
        const taskEntityId = genNodeEntityId('skillResponse');

        const defaultPosition = autoLayout
          ? undefined
          : {
              x: taskStartX,
              y: taskIndex * rowStepY,
            };

        taskIndex++;

        // Regular task: honor the LLM chosen by the planner (task.providerItemId matched
        // against available LLM models); only fall back to the default model when the
        // task did not specify a valid model.
        const taskModelInfo = taskProviderItemId
          ? (llmModels?.find((m) => m.providerItemId === taskProviderItemId) ?? defaultModel)
          : defaultModel;

        // Bind the workflow skill chosen by the planner to this node
        const matchedSkill = taskSkillId
          ? workflowSkills?.find((s) => s.skillId === taskSkillId)
          : undefined;

        // Bind the prompt skills chosen by the planner; unknown IDs are dropped
        const matchedPromptSkills = taskPromptSkillIds
          .map((id) => promptSkills?.find((s) => s.skillId === id))
          .filter((s): s is { skillId: string; title: string; description?: string } => !!s);

        const nodeData: Partial<CanvasNode> = {
          type: 'skillResponse',
          position: defaultPosition,
          data: {
            title: taskTitle,
            editedTitle: taskTitle,
            entityId: taskEntityId,
            contentPreview: '',
            metadata: {
              query: taskPrompt,
              selectedToolsets,
              contextItems: [],
              status: 'init',
              ...(taskModelInfo ? { modelInfo: taskModelInfo } : {}),
              ...(matchedSkill ? { workflowSkill: matchedSkill } : {}),
              ...(matchedPromptSkills.length ? { promptSkills: matchedPromptSkills } : {}),
            },
          },
        };

        const { newNode } = prepareAddNode({
          node: nodeData,
          nodes: [...startNodes, ...nodes] as any[],
          edges: edges as any[],
          connectTo,
          autoLayout,
        });

        nodes.push(newNode);
        taskIdToNodeId.set(taskId, newNode.id);
        taskIdToEntityId.set(taskId, taskEntityId);
      }
    }

    // Phase 2: Create dependency edges
    for (const task of workflowPlan.tasks) {
      const taskId = task?.id ?? `task-${genUniqueId()}`;
      const taskNodeId = taskIdToNodeId.get(taskId);

      if (!taskNodeId) continue;

      // Create edges from dependent tasks to this task
      if (Array.isArray(task.dependentTasks)) {
        for (const dependentTaskId of task.dependentTasks) {
          const sourceNodeId = taskIdToNodeId.get(dependentTaskId);
          if (sourceNodeId && sourceNodeId !== taskNodeId) {
            // Check if edge already exists
            const edgeExists = edges.some(
              (edge) => edge.source === sourceNodeId && edge.target === taskNodeId,
            );

            if (!edgeExists) {
              edges.push({
                id: `edge-${genUniqueId()}`,
                source: sourceNodeId,
                target: taskNodeId,
                type: 'default',
              });
            }
          }
        }
      }
    }
  }

  return {
    nodes,
    edges,
    variables: workflowPlan.variables?.map(planVariableToWorkflowVariable),
  };
};

/**
 * Reverse-convert a canvas state (nodes + edges) back into a WorkflowPlan.
 * Used to package canvas templates (模板库) into reusable workflow skills:
 * - skillResponse node -> regular task (query / selectedToolsets / modelInfo.providerItemId / workflowSkill)
 * - image/video node -> media task (prompt / model / providerItemId)
 * - productionStudio node -> productionStudio task
 * - socialPublish node -> socialPublish task (platformType / postCount / intervalMinutes / publishTargets)
 * - edge source->target means target task depends on source task
 */
export const canvasStateToWorkflowPlan = (
  canvasNodes: CanvasNode[],
  canvasEdges: { source: string; target: string }[],
  variables?: WorkflowVariable[],
  title?: string,
): WorkflowPlan => {
  const tasks: WorkflowTask[] = [];
  const nodeIdToTaskId = new Map<string, string>();

  for (const node of canvasNodes ?? []) {
    const metadata = (node.data?.metadata ?? {}) as any;
    let task: WorkflowTask | null = null;

    if (node.type === 'skillResponse') {
      const toolsets: string[] = (
        (Array.isArray(metadata.selectedToolsets) ? metadata.selectedToolsets : []) as any[]
      )
        .map((t) => t?.id ?? t?.toolset?.key)
        .filter(Boolean);
      task = {
        id: node.id,
        title: node.data?.title ?? '',
        prompt: metadata.query ?? '',
        toolsets,
        ...(metadata.modelInfo?.providerItemId
          ? { providerItemId: metadata.modelInfo.providerItemId }
          : {}),
        ...(metadata.workflowSkill?.skillId ? { skillId: metadata.workflowSkill.skillId } : {}),
      } as WorkflowTask;
    } else if (node.type === 'image' || node.type === 'video') {
      task = {
        id: node.id,
        title: node.data?.title ?? '',
        prompt: metadata.prompt ?? '',
        toolsets: [],
        mediaType: node.type,
        ...(metadata.promptSkills?.length
          ? { promptSkillIds: metadata.promptSkills.map((item: any) => item.skillId) }
          : {}),
        batchCount: 1,
        ...(metadata.model ? { model: metadata.model } : {}),
        ...(metadata.providerItemId ? { providerItemId: metadata.providerItemId } : {}),
      } as WorkflowTask;
    } else if ((node.type as string) === 'socialPublish') {
      task = {
        id: node.id,
        title: node.data?.title ?? '',
        prompt: metadata.prompt ?? metadata.query ?? node.data?.title ?? '',
        toolsets: [],
        nodeType: 'socialPublish',
        platformType: Number(metadata.platformType) || 1,
        accountVariableId: metadata.accountVariableId,
        ...(metadata.mediaVariableId ? { mediaVariableId: metadata.mediaVariableId } : {}),
        ...(metadata.requestVariableId ? { requestVariableId: metadata.requestVariableId } : {}),
        visionProviderItemId: metadata.visionProviderItemId,
        postCount: Math.max(
          1,
          Math.min(10, Number(metadata.postCount) || (metadata.posts?.length ?? 1)),
        ),
        intervalMinutes: Math.max(0, Math.min(1440, Number(metadata.intervalMinutes) || 0)),
        ...(metadata.taskPrompts ? { taskPrompts: metadata.taskPrompts } : {}),
        ...(metadata.taskSettings ? { taskSettings: metadata.taskSettings } : {}),
        ...(Array.isArray(metadata.publishTargets) && metadata.publishTargets.length
          ? { publishTargets: metadata.publishTargets }
          : {}),
      } as unknown as WorkflowTask;
    } else if (node.type === 'productionStudio') {
      task = {
        id: node.id,
        title: node.data?.title ?? '',
        prompt: metadata.request ?? metadata.query ?? node.data?.title ?? '',
        toolsets: [],
        nodeType: 'productionStudio',
        strategy: metadata.strategy ?? 'highlight-montage',
        strategyMarket: metadata.strategyMarket,
        mediaVariableId: metadata.mediaVariableId,
        sourceVariableIds: metadata.sourceVariableIds,
        sourceNodeIds: (metadata.sourceNodeIds ?? [])
          .map((sourceNodeId: string) => nodeIdToTaskId.get(sourceNodeId))
          .filter(Boolean),
        requestVariableId: metadata.requestVariableId,
        providerItemId: metadata.providerItemId,
        targetPlatform: metadata.targetPlatform,
        targetDurationSeconds: metadata.targetDurationSeconds,
        aspectRatio: metadata.aspectRatio,
        resolution: metadata.resolution,
        includeSubtitles: metadata.includeSubtitles,
        musicAssetId: metadata.musicAssetId,
      } as unknown as WorkflowTask;
    }

    if (task) {
      tasks.push(task);
      nodeIdToTaskId.set(node.id, task.id);
    }
  }

  // edges: source -> target 表示 target 依赖 source
  for (const edge of canvasEdges ?? []) {
    const targetTaskId = nodeIdToTaskId.get(edge.target);
    const sourceTaskId = nodeIdToTaskId.get(edge.source);
    if (!targetTaskId || !sourceTaskId) continue;
    const targetTask = tasks.find((t) => t.id === targetTaskId);
    if (!targetTask) continue;
    targetTask.dependentTasks = Array.from(
      new Set([...(targetTask.dependentTasks ?? []), sourceTaskId]),
    );
  }

  return {
    title: title ?? '',
    tasks,
    // 模板只携带输入定义和稳定绑定，不能泄露创建者的素材、账号或私有默认值。
    variables: (variables ?? []).map((variable) => ({ ...variable, value: [] })),
  };
};

// Export converted JSON schemas for tool registration (must be at the end after schema definitions)
import { convertZodToJsonSchema } from './schema-utils';
export const workflowPlanJsonSchema = convertZodToJsonSchema(workflowPlanSchema);
export const workflowPlanPatchJsonSchema = convertZodToJsonSchema(workflowPlanPatchSchema);
